import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getProject, getVersionHistory, restoreVersion, compareVersions, getVersionByCommit } from '../../services/api';
import { getModelDisplayName } from '../../constants/models';
import VersionComparison from './VersionComparison';
import ProjectsTable from './ProjectsTable';
import { useModal } from '../../contexts/ModalContext';
import CustomSelect from '../ui/CustomSelect';

const PROMPT_TYPE_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'System User', label: 'System + User' },
  { value: 'User Only', label: 'User Only' },
  { value: 'few-shot', label: 'Few-Shot Examples' },
];

const VersionControl = ({ projects }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { confirmRestore } = useModal();
  
  const [project, setProject] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setActiveTab] = useState('history');
  const [restoringCommitId, setRestoringCommitId] = useState(null);
  
  // Compare state
  const [compareMode, setCompareMode] = useState(false);
  const [selectedVersions, setSelectedVersions] = useState([null, null]);
  const [comparisonData, setComparisonData] = useState(null);
  const [, setLoadingComparison] = useState(false);
  
  // View state
  const [selectedVersion, setSelectedVersion] = useState(null);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPromptType, setSelectedPromptType] = useState('all');

  const loadData = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const [projectRes, versionsRes] = await Promise.all([
        getProject(id),
        getVersionHistory(id)
      ]);

      if (projectRes.success) {
        setProject(projectRes.data);
      }

      if (versionsRes.success) {
        setVersions(versionsRes.data || []);
      }
    } catch (error) {
      toast.error('Failed to load version control data');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id, loadData]);

  // Handle route-based compare mode - redirect if no versions selected
  useEffect(() => {
    if (location.pathname.endsWith('/compare')) {
      // If on compare route but no versions selected, redirect back
      if (!selectedVersions[0] || !selectedVersions[1]) {
        navigate(`/version-details/${id}`);
      }
    }
  }, [location.pathname, id, selectedVersions, navigate]);

  const handleRestore = async (commitId) => {
    const confirmed = await confirmRestore();
    if (!confirmed) {
      return;
    }

    setRestoringCommitId(commitId);
    try {
      const result = await restoreVersion(id, commitId);
      
      if (result.success && result.data) {
        const newCommitId = result.data.new_commit_id;
        toast.success(`Version restored! New commit: ${newCommitId ? newCommitId.substring(0, 7) : 'created'}`);
        await loadData();
      } else {
        toast.error(result.error || 'Failed to restore version');
      }
    } catch (error) {
      toast.error('Failed to restore version');
    } finally {
      setRestoringCommitId(null);
    }
  };

  const handleViewVersion = async (version) => {
    const result = await getVersionByCommit(version.commit_id);
    if (result.success) {
      setSelectedVersion(result.data);
    } else {
      toast.error('Failed to load version details');
      setSelectedVersion(version);
    }
  };

  const handleSelectForCompare = (version, index) => {
    const newSelected = [...selectedVersions];
    newSelected[index] = version;
    setSelectedVersions(newSelected);

    // Auto-switch to compare tab and navigate if both selected
    if (newSelected[0] && newSelected[1]) {
      setActiveTab('compare');
      loadComparison(newSelected[0], newSelected[1]);
      navigate(`/version-details/${id}/compare`);
    }
  };

  const loadComparison = async (v1, v2) => {
    setLoadingComparison(true);
    try {
      const result = await compareVersions(v1.commit_id, v2.commit_id);
      if (result.success) {
        setComparisonData(result.data);
      }
    } catch (error) {
      toast.error('Failed to load comparison');
    } finally {
      setLoadingComparison(false);
    }
  };

  const handleClearComparison = () => {
    setComparisonData(null);
    setSelectedVersions([null, null]);
    setCompareMode(false);
    if (id) {
      navigate(`/version-details/${id}`);
    }
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffMonths < 12) return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
    return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`;
  };

  const formatDateHeader = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const groupVersionsByDate = (versions) => {
    const grouped = {};
    versions.forEach(version => {
      const dateKey = new Date(version.created_at).toDateString();
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(version);
    });
    return grouped;
  };

  const isCurrentVersion = (commitId) => {
    return commitId === project?.current_commit_id;
  };

  // Filter projects for the list view
  const completedProjects = useMemo(() => {
    return projects?.filter(p => p.status === 'complete') || [];
  }, [projects]);
  
  const filteredProjects = useMemo(() => {
    return completedProjects.filter(proj => {
      // Filter by prompt type
      const projectType = proj.user_input?.type || proj.prompt_type || '';
      let typeMatch = selectedPromptType === 'all';
      
      if (!typeMatch) {
        if (selectedPromptType === 'few-shot') {
          typeMatch = projectType.toLowerCase().includes('assistant') || 
                      projectType.toLowerCase().includes('few') ||
                      projectType.includes('System  User  Assistant');
        } else {
          typeMatch = projectType === selectedPromptType;
        }
      }
      
      // Filter by search query (search in name and description)
      const searchLower = searchQuery.toLowerCase();
      const nameMatch = proj.project_name?.toLowerCase().includes(searchLower);
      const descMatch = proj.user_input?.description?.toLowerCase().includes(searchLower);
      const searchMatch = !searchQuery || nameMatch || descMatch;

      return typeMatch && searchMatch;
    });
  }, [completedProjects, selectedPromptType, searchQuery]);

  const hasActiveFilters = selectedPromptType !== 'all' || searchQuery;

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedPromptType('all');
  };

  // If no ID, show project list
  if (!id) {
    return (
      <div className="h-full flex flex-col bg-transparent overflow-hidden">
        {/* Document Header */}
        <div className="doc-page-header">
          <div className="doc-header-left">
            <div className="doc-header-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
            <div className="doc-header-content">
              <h1 className="doc-header-title">Version Control</h1>
              <p className="doc-header-subtitle">
                <span className="doc-count">{filteredProjects.length}</span>
                {filteredProjects.length === 1 ? ' prompt' : ' prompts'}
                {hasActiveFilters && completedProjects.length !== filteredProjects.length && (
                  <span className="text-text-tertiary"> of {completedProjects.length}</span>
                )}
              </p>
            </div>
          </div>
          
          <div className="doc-header-actions">
            <button onClick={() => navigate('/prompts')} className="doc-new-btn">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
              </svg>
              <span className="hidden sm:inline">Go to Prompts</span>
              <span className="sm:hidden">Prompts</span>
            </button>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="px-4 py-3 border-b border-border-primary/50 flex flex-col sm:flex-row gap-3 sm:items-center">
          {/* Search Input */}
          <div className="relative w-full sm:w-auto sm:min-w-[300px] sm:max-w-[480px]">
            <svg 
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary pointer-events-none" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              type="text"
              placeholder="Search prompts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-bg-secondary border border-border-primary rounded-lg text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            )}
          </div>

          {/* Prompt Type Filter */}
          <div className="relative flex items-center gap-2">
            <div className="flex items-center gap-2 text-text-tertiary">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>
              </svg>
              <span className="text-xs font-medium hidden sm:inline">Filter:</span>
            </div>
            <CustomSelect
              value={selectedPromptType}
              onChange={(e) => setSelectedPromptType(e.target.value)}
              options={PROMPT_TYPE_OPTIONS}
              placeholder="All Types"
              size="small"
            />
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="px-3 py-2 text-xs font-medium text-text-secondary hover:text-text-primary bg-bg-tertiary hover:bg-bg-secondary border border-border-primary rounded-lg transition-all flex items-center gap-1.5"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 18L18 6M6 6l12 12"/>
              </svg>
              Clear
            </button>
          )}
        </div>

        {/* Document List */}
        <ProjectsTable
          projects={filteredProjects}
          onSelectProject={(proj) => navigate(`/version-details/${proj.id}`)}
          emptyStateConfig={{
            title: hasActiveFilters ? 'No Matching Prompts' : 'No Prompts Available',
            description: hasActiveFilters 
              ? 'No prompts match your current filters. Try adjusting your search or filter criteria.'
              : 'Create and complete your first prompt to view version history and manage your prompt versions.',
            icon: (props) => (
              <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
            ),
            actionLabel: hasActiveFilters ? 'Clear Filters' : 'Create a Prompt',
            onAction: hasActiveFilters ? clearFilters : () => navigate('/prompts')
          }}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="h-full flex flex-col bg-bg-primary overflow-hidden">
        <div className="flex-1 flex flex-col items-center justify-center gap-4 p-10 text-center">
          <div className="w-10 h-10 border-2 border-border-primary border-t-primary-500 rounded-full animate-spin"></div>
          <p className="text-text-secondary text-base mt-4">Loading version control...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-bg-primary overflow-hidden">
      {/* Header */}
      <div className="pd-header">
        <button className="btn-back" onClick={() => navigate('/versions')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back to Versions
        </button>
        
        <div className="pd-header-content">
          <div className="pd-title-row">
            <h2 className="pd-title">{project?.project_name || 'Prompt'}</h2>
          </div>
          <div className="pd-metadata">
            {project?.created_at && (
              <span className="pd-meta-item">
                <span className="pd-meta-label">Created:</span>
                <span className="pd-meta-value">{new Date(project.created_at).toLocaleString()}</span>
              </span>
            )}
            {project?.current_commit_id && (
              <span className="pd-meta-item">
                <span className="pd-meta-label">Current Version:</span>
                <code className="pd-commit-id">{project.current_commit_id.substring(0, 7)}</code>
              </span>
            )}
          </div>
        </div>

        <div className="pd-actions">
          <button 
            className={`btn-action-secondary ${compareMode ? 'active' : ''}`}
            onClick={() => {
              if (compareMode) {
                handleClearComparison();
              } else {
                setCompareMode(true);
              }
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
              <polyline points="15 3 21 3 21 9"/>
              <line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
            {compareMode ? 'Cancel Compare' : 'Compare Versions'}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="pd-content">
        {comparisonData && location.pathname.endsWith('/compare') ? (
          <VersionComparison
            version1={comparisonData.version1}
            version2={comparisonData.version2}
            onClose={handleClearComparison}
          />
        ) : (
          <>
            {versions.length === 0 ? (
              <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-16">
                <div className="w-16 h-16 rounded-full bg-bg-tertiary flex items-center justify-center mb-4 border border-border-primary">
                  <svg className="w-8 h-8 text-text-tertiary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                </div>
                <p className="text-text-secondary text-base">No version history available</p>
              </div>
            ) : (
              <div className="pd-input-section">
                {Object.entries(groupVersionsByDate(versions)).map(([dateKey, dateVersions]) => (
                  <div key={dateKey} className="mb-6">
                    <h3 className="pd-section-title flex items-center gap-2 mb-4">
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      {formatDateHeader(dateVersions[0].created_at)}
                    </h3>
                      
                    {dateVersions.map((version) => {
                        const isCurrent = isCurrentVersion(version.commit_id);
                        const isDraft = version.status === 'draft';
                        const isRestoring = restoringCommitId === version.commit_id;

                        return (
                          <div 
                            key={version.commit_id}
                            className={`flex items-center gap-3 py-2.5 px-3 mb-1 rounded-md transition-all hover:bg-bg-hover ${isCurrent ? 'border-l-2 border-l-primary-500 bg-primary-500/5' : ''} ${isDraft ? 'opacity-70' : ''}`}
                          >
                            {/* Commit ID */}
                            <code className="font-mono text-xs font-semibold text-primary-400 w-16 flex-shrink-0">{version.commit_id.substring(0, 7)}</code>
                            
                            {/* Message */}
                            <span className="flex-1 text-sm text-text-primary truncate">
                              {version.commit_message || 'No commit message'}
                            </span>
                            
                            {/* Meta info */}
                            <div className="hidden sm:flex items-center gap-2 text-xs text-text-tertiary flex-shrink-0">
                              <span>{formatRelativeTime(version.created_at)}</span>
                              {version.created_by?.username && (
                                <>
                                  <span className="text-text-disabled">•</span>
                                  <span>{version.created_by.username}</span>
                                </>
                              )}
                            </div>
                            
                            {/* Actions */}
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              {compareMode ? (
                                <>
                                  <button
                                    className={`px-2 py-1 text-xs font-medium rounded transition-all ${selectedVersions[0]?.commit_id === version.commit_id ? 'bg-primary-600 text-white' : 'bg-bg-tertiary text-text-secondary hover:bg-bg-hover'}`}
                                    onClick={() => handleSelectForCompare(version, 0)}
                                    disabled={selectedVersions[1]?.commit_id === version.commit_id}
                                  >
                                    {selectedVersions[0]?.commit_id === version.commit_id ? '✓ V1' : 'V1'}
                                  </button>
                                  <button
                                    className={`px-2 py-1 text-xs font-medium rounded transition-all ${selectedVersions[1]?.commit_id === version.commit_id ? 'bg-primary-600 text-white' : 'bg-bg-tertiary text-text-secondary hover:bg-bg-hover'}`}
                                    onClick={() => handleSelectForCompare(version, 1)}
                                    disabled={selectedVersions[0]?.commit_id === version.commit_id}
                                  >
                                    {selectedVersions[1]?.commit_id === version.commit_id ? '✓ V2' : 'V2'}
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button 
                                    className="p-1.5 text-text-tertiary hover:text-text-primary hover:bg-bg-hover rounded transition-all"
                                    onClick={() => handleViewVersion(version)}
                                    title="View version details"
                                  >
                                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                      <circle cx="12" cy="12" r="3"/>
                                    </svg>
                                  </button>
                                  {isCurrent && (
                                    <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-primary-600/20 text-primary-400">
                                      Current
                                    </span>
                                  )}
                                  {isDraft && (
                                    <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-yellow-600/20 text-yellow-400">
                                      Draft
                                    </span>
                                  )}
                                  {!isCurrent && !isDraft && (
                                    <button
                                      className="px-2 py-1 text-xs font-medium text-text-secondary hover:text-text-primary bg-bg-tertiary hover:bg-bg-hover rounded transition-all"
                                      onClick={() => handleRestore(version.commit_id)}
                                      disabled={isRestoring}
                                    >
                                      {isRestoring ? '...' : 'Restore'}
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Version Detail Modal */}
      {selectedVersion && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-bg-primary/60 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setSelectedVersion(null)}
        >
          <div 
            className="bg-bg-secondary border border-border-primary rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border-primary bg-bg-tertiary/50">
              <h3 className="text-lg font-semibold text-text-primary">Version Details</h3>
              <button 
                onClick={() => setSelectedVersion(null)}
                className="p-2 text-text-secondary hover:text-text-primary hover:bg-bg-hover rounded-lg transition-all duration-200"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Metadata Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-bg-primary border border-border-primary rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-2">Commit ID</h4>
                  <code className="text-sm font-mono text-primary-500 font-semibold">{selectedVersion.commit_id.substring(0, 12)}</code>
                </div>
                <div className="bg-bg-primary border border-border-primary rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-2">Created</h4>
                  <p className="text-sm text-text-primary">{new Date(selectedVersion.created_at).toLocaleString()}</p>
                </div>
                <div className="bg-bg-primary border border-border-primary rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-2">Model</h4>
                  <p className="text-sm text-text-primary">{getModelDisplayName(selectedVersion.model)}</p>
                </div>
              </div>

              {/* Commit Message */}
              {selectedVersion.commit_message && (
                <div className="bg-bg-primary border border-border-primary rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
                    <svg className="w-4 h-4 text-text-tertiary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="17 8 12 3 7 8"/>
                      <line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                    Commit Message
                  </h4>
                  <p className="text-sm text-text-secondary leading-relaxed">{selectedVersion.commit_message}</p>
                </div>
              )}

              {/* Prompts */}
              {selectedVersion.system_prompt_output && (
                <div className="bg-bg-primary border border-border-primary rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-text-primary mb-3">System Prompt</h4>
                  <pre className="text-xs text-text-secondary bg-bg-tertiary border border-border-primary rounded-lg p-4 overflow-x-auto font-mono leading-relaxed whitespace-pre-wrap">{selectedVersion.system_prompt_output}</pre>
                </div>
              )}

              <div className="bg-bg-primary border border-border-primary rounded-lg p-4">
                <h4 className="text-sm font-semibold text-text-primary mb-3">User Prompt</h4>
                <pre className="text-xs text-text-secondary bg-bg-tertiary border border-border-primary rounded-lg p-4 overflow-x-auto font-mono leading-relaxed whitespace-pre-wrap">{selectedVersion.user_prompt_output || 'No user prompt'}</pre>
              </div>

              {selectedVersion.few_shot_examples && (
                <div className="bg-bg-primary border border-border-primary rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-text-primary mb-3">Few-Shot Examples</h4>
                  <pre className="text-xs text-text-secondary bg-bg-tertiary border border-border-primary rounded-lg p-4 overflow-x-auto font-mono leading-relaxed whitespace-pre-wrap">{selectedVersion.few_shot_examples}</pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

VersionControl.propTypes = {
  projects: PropTypes.array,
};

VersionControl.defaultProps = {
  projects: [],
};

export default VersionControl;

